<template>
  <section
    class="publication-rules"
    :class="{ 'publication-rules--compact': compact }"
    :aria-labelledby="compact ? undefined : 'publication-rules-title'"
    :aria-label="compact ? 'Confirmation de la Charte de publication' : undefined"
  >
    <header v-if="!compact" class="publication-rules__header">
      <p class="publication-rules__eyebrow">Quelques idées pour démarrer</p>
      <h3 id="publication-rules-title">Quel genre de quête lancer ?</h3>
      <p>
        Une bonne quête est simple : on comprend qui aider, pour quoi, où et
        combien de temps.
      </p>
    </header>

    <div v-if="!compact" class="publication-rules__good">
      <h4><span aria-hidden="true">●</span> Les quêtes qui font vivre le quartier</h4>
      <div class="publication-rules__ideas">
        <article>
          <b>Coup de pouce du quotidien</b>
          <span>Porter des courses, monter un meuble simple ou arroser des plantes pendant une absence.</span>
        </article>
        <article>
          <b>Solidarité locale</b>
          <span>Préparer une collecte, aider une association ou accueillir les participants d’un événement.</span>
        </article>
        <article>
          <b>Quartier plus agréable</b>
          <span>Ramassage citoyen, jardin partagé ou petite installation pour une fête de quartier.</span>
        </article>
        <article>
          <b>Partager un savoir</b>
          <span>Montrer comment utiliser un smartphone, animer un atelier créatif ou partager une recette.</span>
        </article>
        <article>
          <b>Créer du lien</b>
          <span>Faire découvrir le quartier, rendre visite à une personne isolée ou accompagner une démarche.</span>
        </article>
        <article>
          <b>Petits imprévus</b>
          <span>Retrouver un animal, récupérer un objet ou donner un coup de main ponctuel sans danger.</span>
        </article>
      </div>
    </div>

    <div v-if="!compact" class="publication-rules__redirect">
      <h4><span aria-hidden="true">●</span> À orienter vers le bon interlocuteur</h4>
      <ul>
        <li><b>Secours et santé :</b> urgences, soins ou médicaments relèvent des services d’urgence et des professionnels.</li>
        <li><b>Travaux techniques ou dangereux :</b> gaz, électricité, toiture, machines ou produits chimiques demandent un professionnel qualifié.</li>
        <li><b>Un véritable emploi :</b> une tâche régulière ou le remplacement d’un salarié doit devenir un emploi ou une prestation déclarée.</li>
        <li><b>Données et argent :</b> aucun mot de passe, coordonnée bancaire, pièce d’identité ni demande d’argent dans une quête.</li>
      </ul>
      <p>
        Ces demandes ne sont pas des quêtes que la Guilde accepte. Il faudra
        vous tourner vers le bon interlocuteur. N’hésitez pas à demander à Lyra.
      </p>
    </div>

    <p v-if="!compact" class="publication-rules__help">
      Un doute ? Demandez à Lyra : elle peut vous aider à formuler votre besoin
      ou à trouver le bon interlocuteur avant publication.
    </p>

    <label class="publication-rules__accept">
      <input
        :checked="modelValue"
        type="checkbox"
        @change="handleAccepted"
      />
      <span>
        Je confirme que cette quête est ponctuelle, sûre et qu’elle ne remplace
        ni les secours ni un emploi.
        <a href="/mentions-legales#charte-publication" target="_blank" rel="noopener noreferrer">Lire la Charte de publication</a>
      </span>
    </label>
  </section>
</template>

<script setup lang="ts">
withDefaults(defineProps<{ modelValue: boolean; compact?: boolean }>(), { compact: false });
const emit = defineEmits<{ (event: 'update:modelValue', value: boolean): void }>();

function handleAccepted(event: Event) {
  emit('update:modelValue', (event.target as HTMLInputElement).checked);
}
</script>

<style scoped>
.publication-rules {
  display: grid;
  gap: 14px;
  padding: clamp(16px, 2.4vw, 24px);
  border: 1px solid rgba(157, 116, 53, .42);
  border-radius: 18px 8px;
  color: inherit;
  background:
    radial-gradient(circle at 92% 0, rgba(220, 177, 91, .13), transparent 18rem),
    linear-gradient(135deg, rgba(72, 54, 30, .13), rgba(20, 45, 38, .1));
}
.publication-rules--compact { padding: 0; border: 0; background: transparent; }
.publication-rules__header { display: grid; gap: 5px; }
.publication-rules__eyebrow {
  margin: 0;
  color: #b9852d;
  font-size: .68rem;
  font-weight: 900;
  letter-spacing: .13em;
  text-transform: uppercase;
}
.publication-rules h3 { margin: 0; color: inherit; font-family: var(--font-title, Georgia, serif); font-size: clamp(1.25rem, 2vw, 1.65rem); }
.publication-rules__header > p:last-child { margin: 0; opacity: .78; font-size: .84rem; line-height: 1.55; }
.publication-rules__good {
  display: grid;
  gap: 11px;
  padding: 14px;
  border: 1px solid rgba(48, 143, 86, .42);
  border-radius: 13px 6px;
  background: rgba(43, 124, 75, .1);
}
.publication-rules__good h4 { margin: 0; color: #3a9f63; font-size: .88rem; }
.publication-rules__good h4 span { margin-right: 5px; }
.publication-rules__ideas { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 9px 14px; }
.publication-rules__ideas article { display: grid; gap: 2px; padding-left: 13px; border-left: 2px solid rgba(58, 159, 99, .52); }
.publication-rules__ideas b { font-size: .78rem; }
.publication-rules__ideas span { opacity: .76; font-size: .71rem; line-height: 1.45; }
.publication-rules__redirect {
  padding: 10px 12px;
  border: 1px solid rgba(177, 80, 69, .28);
  border-radius: 10px 5px;
  background: rgba(143, 56, 48, .06);
}
.publication-rules__redirect h4 { margin: 0; color: #b75b50; font-size: .76rem; font-weight: 800; }
.publication-rules__redirect h4 span { margin-right: 5px; }
.publication-rules__redirect ul { display: grid; gap: 6px; margin: 10px 0 0; padding-left: 18px; }
.publication-rules__redirect li { opacity: .76; font-size: .69rem; line-height: 1.45; }
.publication-rules__redirect p { margin: 9px 0 0; color: #a64f45; font-size: .69rem; font-weight: 700; line-height: 1.45; }
.publication-rules__help { margin: 0; opacity: .7; font-size: .71rem; font-style: italic; }
.publication-rules__accept { display: flex; align-items: flex-start; gap: 10px; padding: 12px 13px; border: 1px solid rgba(157, 116, 53, .34); border-radius: 9px; background: rgba(210, 166, 79, .08); cursor: pointer; }
.publication-rules .publication-rules__accept input { flex: 0 0 auto; width: 18px; height: 18px; margin-top: 1px; accent-color: #3a9f63; }
.publication-rules__accept span { font-size: .75rem; line-height: 1.5; }
.publication-rules__accept a { display: inline-block; margin-left: 5px; color: #b9852d; font-weight: 800; text-decoration-thickness: 1px; text-underline-offset: 2px; }
@media (max-width: 680px) {
  .publication-rules__ideas { grid-template-columns: 1fr; }
}
</style>
